import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"

dayjs.extend(utc)

// Event start times: one place to read, show and send `start_datetime`.
//
// The API returns start_datetime like "2026-11-03T21:30:00.000000Z". The
// digits are the event's local time in Saudi Arabia (Asia/Riyadh); the
// trailing "Z" is wrong, but old mobile apps depend on the format, so it
// stays. The backend stores the digits it receives and drops any offset.
//
//   • Show the digits as they are — never convert them to the viewer's
//     time zone.
//   • Send the digits of the wall-clock time the user picked.
//   • When a real moment in time is needed (calendar entries), read the
//     digits as Riyadh time.
//   • "Today" / "past" compare the event's date digits with today's date
//     in Riyadh.

const HOUR_MS = 60 * 60 * 1000
const DAY_MS = 24 * HOUR_MS
// Saudi Arabia is UTC+3 all year (no daylight saving), so a fixed offset is exact.
const RIYADH_OFFSET_MS = 3 * HOUR_MS

// Events carry no end time, so an "Add to calendar" entry lasts this long
// (same as the backend's iCal file, EventCalendarFile::DEFAULT_HOURS).
export const CALENDAR_ENTRY_HOURS = 3

export interface EventStartParts {
  year: number
  month: number // 1-12
  day: number
  hour: number
  minute: number
  second: number
}

// "2026-11-03T21:30:00.000000Z", "2026-11-03 21:30:00", "2026-11-03T21:30",
// "2026-11-03". A trailing Z or offset is ignored: the digits are what count.
const START_PATTERN =
  /^\s*(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:[.,]\d+)?)?)?\s*(?:Z|[+-]\d{2}(?::?\d{2})?)?\s*$/i

// A Date whose UTC fields are the event's digits. Only ever read it in UTC.
const digitsAsUtc = (p: EventStartParts): Date =>
  new Date(Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second))

/** The digits of an API start_datetime, or null when there is no usable value. */
export const parseEventStart = (value: unknown): EventStartParts | null => {
  if (typeof value !== "string") return null
  const match = START_PATTERN.exec(value)
  if (!match) return null
  const [year, month, day, hour, minute, second] = match
    .slice(1, 7)
    .map((part) => (part === undefined ? 0 : Number(part)))
  const check = digitsAsUtc({ year, month, day, hour, minute, second })
  // Reject impossible values ("2026-02-30", "25:00") instead of rolling over.
  if (
    check.getUTCFullYear() !== year ||
    check.getUTCMonth() !== month - 1 ||
    check.getUTCDate() !== day ||
    check.getUTCHours() !== hour ||
    check.getUTCMinutes() !== minute ||
    check.getUTCSeconds() !== second
  ) {
    return null
  }
  return { year, month, day, hour, minute, second }
}

/**
 * An event's start exactly as entered (Saudi local time), whatever the
 * viewer's time zone. Takes Intl.DateTimeFormat options; "" when there is no
 * usable value.
 */
export const formatEventStart = (
  value: unknown,
  locale: string | string[] | undefined,
  options: Intl.DateTimeFormatOptions,
): string => {
  const parts = parseEventStart(value)
  if (!parts) return ""
  return new Intl.DateTimeFormat(locale, { ...options, timeZone: "UTC" }).format(
    digitsAsUtc(parts),
  )
}

/** Same as formatEventStart, with a dayjs pattern such as "dddd, DD MMM YYYY". */
export const formatEventStartPattern = (value: unknown, pattern: string): string => {
  const parts = parseEventStart(value)
  if (!parts) return ""
  return dayjs.utc(digitsAsUtc(parts)).format(pattern)
}

/** The real moment the event starts (its digits read as Riyadh time), or null. */
export const eventStartInstant = (value: unknown): Date | null => {
  const parts = parseEventStart(value)
  if (!parts) return null
  return new Date(digitsAsUtc(parts).getTime() - RIYADH_OFFSET_MS)
}

/** Start and end of an "Add to calendar" entry, as real moments in time. */
export const eventCalendarSpan = (
  value: unknown,
): { start: Date; end: Date } | null => {
  const start = eventStartInstant(value)
  if (!start) return null
  return {
    start,
    end: new Date(start.getTime() + CALENDAR_ENTRY_HOURS * HOUR_MS),
  }
}

/** Today's date in Riyadh. */
export const riyadhToday = (
  now: Date = new Date(),
): { year: number; month: number; day: number } => {
  const riyadh = new Date(now.getTime() + RIYADH_OFFSET_MS)
  return {
    year: riyadh.getUTCFullYear(),
    month: riyadh.getUTCMonth() + 1,
    day: riyadh.getUTCDate(),
  }
}

/**
 * Whole days from today (in Riyadh) to the event's date: 0 = today,
 * 1 = tomorrow, -1 = yesterday. Null when there is no usable start.
 */
export const eventDayOffset = (
  value: unknown,
  now: Date = new Date(),
): number | null => {
  const parts = parseEventStart(value)
  if (!parts) return null
  const today = riyadhToday(now)
  const eventDay = Date.UTC(parts.year, parts.month - 1, parts.day)
  const todayDay = Date.UTC(today.year, today.month - 1, today.day)
  return Math.round((eventDay - todayDay) / DAY_MS)
}

// --- Date pickers -----------------------------------------------------------
// react-datepicker works with local Dates: the picker gets a local Date whose
// fields are the API digits, and a picked Date goes back out as its local
// fields. Both use the local constructor/getters, never offset arithmetic:
// an offset read at the wrong instant is an hour off around a DST change
// (a 21:00 start on 29 Oct 2026 read 22:00 in a Cairo browser).

// "2026-11-03T21:30:00.000000Z" from the digits.
const formatApiStart = (p: EventStartParts): string => {
  const pad = (n: number, width = 2) => String(n).padStart(width, "0")
  return (
    `${pad(p.year, 4)}-${pad(p.month)}-${pad(p.day)}` +
    `T${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}.000000Z`
  )
}

/**
 * The Date to give react-datepicker (or to format in local time) for an API
 * start_datetime: its local fields are the digits. A time the browser's clock
 * skips (the hour lost when clocks go forward) comes back an hour later.
 */
export const apiStartToPickerDate = (
  value: string | null | undefined,
): Date | null => {
  const p = parseEventStart(value)
  if (!p) return null
  return new Date(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
}

/**
 * The start_datetime to send for a Date picked in react-datepicker: the
 * wall-clock digits the user picked, e.g. "2026-11-03T21:30:00.000000Z".
 */
export const pickerDateToApiStart = (date: Date | null | undefined): string => {
  if (!date || isNaN(date.getTime())) return ""
  return formatApiStart({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
    hour: date.getHours(),
    minute: date.getMinutes(),
    second: date.getSeconds(),
  })
}

/**
 * The start_datetime to send for a value the user picked, whether it is
 * still a picker Date or already an API-style string. "" when unusable.
 */
export const toApiStart = (value: unknown): string => {
  if (value instanceof Date) return pickerDateToApiStart(value)
  const parts = parseEventStart(value)
  return parts ? formatApiStart(parts) : ""
}
